import "ol/ol.css";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { createRoot, Root } from 'react-dom/client';
import { Feature, Map as OlMap } from "ol";
import View from 'ol/View';
import Overlay, { Positioning } from 'ol/Overlay';
import { fromLonLat, transformExtent } from 'ol/proj';
import {
    Circle as CircleStyle,
    Fill,
    Stroke,
    Style,
    Text as OlText,
} from 'ol/style';
import { OSM, Vector as VectorSource, Cluster } from 'ol/source';
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import SensorStatusBoard from "../SensorStatusBoard";
import { SensorDataDto, useGetSensorDataQuery } from "../../api/sensorDataApi";
import { useAppDispatch, useAppSelector } from "../../../../shared/store/store";
import MapStyle from "./css/Map.module.css";
import { Point } from "ol/geom";
import Bubble from "../../../../shared/components/Bubble";
import Text from "../../../../shared/components/Text";
import Padding from "../../../../shared/components/Padding";
import MarkerInfo from "./MarkerInfo";
import { setSelectedSensor as setDashboardSelectedSensor, setIsStatusBoardOpen, setMapExtent, setVisibleMarkerCounts, setShouldFitOnFilterChange } from "../../slice/sensorDashboardSlice";
import SensorStatusFloating from "../SensorStatusFloating";
import SensorStatusMobile from "../SensorStatusMobile";
import useGetStatusMessage from "../../hooks/useStatusFilter";
import statusToColor from "../../utils/statusToColor";
import IcoGeolocation from "../../../../assets/img/IcoGeolocation";
import { setSelectedSensor as setSelectedSensorReducer } from "../../slice/sensorDashboardSlice";
import useDevice from "../../../../shared/hooks/useDevice";
import Loading from "../../../../shared/components/Loading";
import { useLocation } from "react-router-dom";
import { useAlert } from "../../../../shared/components/AlertProvider";
import { SENSOR_DATA_POLLING_INTERVAL } from "../../constants/pollingConfig";


function Map() {
    const dispatch = useAppDispatch();
    // API 호출
    const sensorDashboardFilter = useAppSelector(state => state.sensorDashboard.filter);
    const shouldFitOnFilterChange = useAppSelector(state => state.sensorDashboard.shouldFitOnFilterChange);
    const { processingMap, isEmpty } = useGetStatusMessage();
    const searchKeyword = useAppSelector(state => state.sensorDashboard.searchKeyword);
    const sensorData = useAppSelector(state => state.sensorData);
    // 현재 필터가 적용된 센서 데이터(지도 표시용)

    // 필터가 적용된 센서 데이터 조회 (지도 표시용)
    const {
        data: rawSensorData,
        currentData: rawCurrentSensorData,
        isSuccess: isSensorDataSuccess,
        originalArgs,
    } = useGetSensorDataQuery({
        status: "",
        searchKeyword: searchKeyword,
    }, {
        refetchOnFocus: true,
        refetchOnMountOrArgChange: true,
        refetchOnReconnect: true,
        pollingInterval: SENSOR_DATA_POLLING_INTERVAL,
        skipPollingIfUnfocused: false,
    });
    // 현재 화면에 그리는 센서 데이터(필터 적용)와 별개로, 카운트 집계를 위한 전체 센서 데이터
    const {
        data: rawAllCurrentSensorData,
        isSuccess: isAllSensorDataSuccess,
    } = useGetSensorDataQuery({
        status: "",
        searchKeyword: searchKeyword,
    }, {
        refetchOnFocus: true,
        refetchOnMountOrArgChange: true,
        refetchOnReconnect: true,
        pollingInterval: SENSOR_DATA_POLLING_INTERVAL,
        skipPollingIfUnfocused: false,
    });

    // 현재 지도에 표시할 데이터(필터 미적용, 검색만 적용)
    const displayedSensorData = useMemo(() => {
        return (rawCurrentSensorData as SensorDataDto[] | undefined)
            ?? (rawSensorData as SensorDataDto[] | undefined)
            ?? [];
    }, [rawCurrentSensorData, rawSensorData]);

    // 이전 데이터를 유지하면서 새 데이터가 들어오면 업데이트
    const [forceHideLoading, setForceHideLoading] = useState(false);
    const [hasInitialLoaded, setHasInitialLoaded] = useState(false);
    // 현재 표시된 센서 데이터가 어떤 필터로 조회된 것인지 추적하기 위한 ref
    const displayedSensorDataArgsRef = useRef<{ status?: string } | undefined>(undefined);
    const openAlert = useAlert();

    // 4초 후 로딩 강제 숨김
    useEffect(() => {
        const timer = setTimeout(() => {
            setForceHideLoading(true);
        }, 4000);
        return () => clearTimeout(timer);
    }, []);
    const dashboardSelectedSensor = useAppSelector(state => state.sensorDashboard.selectedSensor);
    const isStatusBoardOpen = useAppSelector(state => state.sensorDashboard.isStatusBoardOpen);

    // 디바이스 체크
    const device = useDevice();
    const isMobile = device === "mobile";
    const isMobileRef = useRef(isMobile);
    const processingMapRef = useRef(processingMap);
    // 지도 기본 상수 (isMobile에 따라 동적으로 계산)
    const MAP_EXTENT = useMemo(() => {
        // 대한민국 전체 범위: [경도(서), 위도(남), 경도(동), 위도(북)]
        return [12806977.001254011, 3851176.7469184576, 15611298.694980558, 4954315.939130121];
    }, [isMobile]);

    const [olMap, setOlMap] = useState<OlMap | null>(null);
    const [selectedSensor, setSelectedSensor] = useState<SensorDataDto | null>(null);
    const zoomBucketRef = useRef<string | null>(null);
    const isOverlayVisibleRef = useRef<boolean>(false); // 줌 레벨이 10을 넘어서는지 추적
    const olMapRef = useRef<HTMLDivElement>(null);
    const overlayRootsRef = useRef<globalThis.Map<string, Root>>(new globalThis.Map<string, Root>());
    const overlayRefs = useRef<globalThis.Map<string, Overlay>>(new globalThis.Map<string, Overlay>());
    const infoOverlayElementRef = useRef<HTMLDivElement>(null);
    const infoOverlayRef = useRef<Overlay | null>(null);
    const prevFilterRef = useRef<string>(sensorDashboardFilter);
    const clusterSourceRef = useRef<Cluster | null>(null);
    const clusterMembershipRef = useRef<globalThis.Map<string, number>>(new globalThis.Map<string, number>());
    const myLocationOverlayRef = useRef<Overlay | null>(null);
    const myLocationElementRef = useRef<HTMLDivElement | null>(null);
    const locationTrackingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const location = useLocation();

    // sensorData를 ref로 저장하여 이벤트 리스너에서 항상 최신 값 참조
    const sensorDataRef = useRef<SensorDataDto[]>([]);
    const allSensorDataRef = useRef<SensorDataDto[]>([]);
    // 줌 애니메이션 진행 중인지 추적하는 ref
    const isZoomAnimatingRef = useRef<boolean>(false);
    const skipNextDashboardFocusRef = useRef<boolean>(false);
    // setFeatures 내의 setTimeout 타이머를 추적하는 ref (빠른 필터 전환 시 취소용)
    const setFeaturesTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    // useEffect 내의 setTimeout 타이머를 추적하는 ref
    const updateFeaturesTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    // 줌 변경 시 overlay 업데이트를 최신 함수로 호출하기 위한 ref
    const updateOverlaysForZoomRef = useRef<() => void>(() => {});

    // isMobile 값이 변경될 때마다 ref 업데이트
    useEffect(() => {
        isMobileRef.current = isMobile;
    }, [isMobile]);

    // sensorData가 변경될 때마다 ref 업데이트
    // 필터가 적용된 최신 데이터를 ref로 보관 (쿼리 데이터 우선, 없으면 store 데이터)
    useEffect(() => {
        const latest = displayedSensorData;
        if (latest) {
            sensorDataRef.current = latest;
        }
    }, [displayedSensorData]);

    // 필터 적용 데이터가 로드되면 현재 필터 인자를 기록 (fit 조건 판단용)
    useEffect(() => {
        if (isSensorDataSuccess && (rawCurrentSensorData || rawSensorData)) {
            displayedSensorDataArgsRef.current = {
                status: (originalArgs as any)?.status ?? processingMap.get(sensorDashboardFilter) ?? ""
            };
        }
    }, [isSensorDataSuccess, sensorDashboardFilter, processingMap, rawCurrentSensorData, rawSensorData, originalArgs]);

    // 전체 센서 데이터가 변경될 때마다 ref 업데이트 (집계용)
    useEffect(() => {
        if (rawAllCurrentSensorData) {
            allSensorDataRef.current = rawAllCurrentSensorData;
            if (olMap) {
                updateVisibleMarkerCounts(olMap, rawAllCurrentSensorData);
            }
        }
    }, [rawAllCurrentSensorData, olMap]);

    // processingMap이 변경될 때마다 ref 업데이트
    useEffect(() => {
        processingMapRef.current = processingMap;
    }, [processingMap]);

    // 상태별 마커 스타일 캐시 (미리 생성하여 재사용)
    const markerStyleCache = useMemo(() => {
        const cache = new globalThis.Map<string, Style>();
        const statusGroups = ["안전", "주의", "경보", "점검"];

        statusGroups.forEach(statusGroup => {
            const statusColor = statusToColor(statusGroup);
            const style = new Style({
                image: new CircleStyle({
                    radius: 10,
                    fill: new Fill({
                        color: statusColor.color,
                    }),
                    stroke: new Stroke({
                        color: '#ffffff',
                        width: 2,
                    }),
                }),
            });
            cache.set(statusGroup, style);
        });

        // 기본 스타일 (상태가 없는 경우)
        const defaultColor = statusToColor("");
        const defaultStyle = new Style({
            image: new CircleStyle({
                radius: 8,
                fill: new Fill({
                    color: defaultColor.color,
                }),
                stroke: new Stroke({
                    color: '#ffffff',
                    width: 2,
                }),
            }),
        });
        cache.set("", defaultStyle);

        return cache;
    }, []);

    // 개별 마커 스타일 (캐시에서 가져오기)
    function getMarkerStyle(sensor: SensorDataDto) {
        const statusGroup = processingMap.get(sensor.sensorStatusGrp) ?? "";
        return markerStyleCache.get(statusGroup) || markerStyleCache.get("")!;
    }

    // 클러스터 정보를 저장하는 ref (센서 IMEI -> 클러스터 정보)
    const clusterInfoRef = useRef<globalThis.Map<string, { sensors: SensorDataDto[], index: number }>>(new globalThis.Map<string, { sensors: SensorDataDto[], index: number }>());

    // 클러스터 스타일
    const clusterStyle = useMemo(() => {
        return new Style({
            image: new CircleStyle({
                radius: 55,
                fill: new Fill({ color: 'rgba(113, 144, 224, 0.8)' }),
                stroke: new Stroke({ color: 'rgba(0,0,0,0.35)', width: 1.5 }),
            }),
            text: new OlText({
                text: '',
                fill: new Fill({ color: '#ffffff' }),
                stroke: new Stroke({ color: 'rgba(0,0,0,0.4)', width: 2 }),
                font: '600 14px sans-serif',
            }),
        });
    }, []);

    // Cluster mode: enabled below zoom 12
    const isClusterMode = (zoom: number) => zoom < 12;
    // Sensor Marker Ping Overlay 추가
    const sensorPingOverlayRefs = useRef<globalThis.Map<string, Overlay>>(new globalThis.Map<string, Overlay>());

    function addSensorPingOverlay(sensor: SensorDataDto) {
        if (!olMap) return;

        // 기존 ping overlay가 있으면 건너뛰기
        if (sensorPingOverlayRefs.current.has(sensor.imei)) return;

        // Ping div 생성 (내 위치 마커와 동일한 방식)
        const pingElement = document.createElement('div');
        pingElement.className = MapStyle.sensorMarkerPing;

        // 센서 상태에 따른 색상 설정
        const statusGroup = processingMapRef.current.get(sensor.sensorStatusGrp) ?? "";
        const statusColor = statusToColor(statusGroup);
        // rgba에서 투명도를 0.3으로 조정
        const colorWithAlpha = statusColor.color.replace('1)', '0.3)');
        pingElement.style.backgroundColor = colorWithAlpha;

        // Overlay 생성
        const pingOverlay = new Overlay({
            element: pingElement,
            position: fromLonLat([sensor.sensorLot, sensor.sensorLat]) as [number, number],
            positioning: 'center-center',
            stopEvent: false,
        });

        olMap.addOverlay(pingOverlay);
        sensorPingOverlayRefs.current.set(sensor.imei, pingOverlay);
    }

    const focusToSensor = useCallback((sensor: SensorDataDto) => {
        if (!olMap) return;

        const view = olMap.getView();
        const offsetX = isMobileRef.current ? 0 : 0.0015;
        const offsetY = isMobileRef.current ? 0 : 0.0006;
        const targetPosition = fromLonLat([sensor.sensorLot - offsetX, sensor.sensorLat - offsetY]) as [number, number];
        const currentZoom = view.getZoom() ?? 6;
        const targetZoom = currentZoom < 17 ? 17 : currentZoom;

        isZoomAnimatingRef.current = true;

        view.cancelAnimations();
        view.animate({
            center: targetPosition,
            duration: 1000,
            zoom: targetZoom
        }, () => {
            isZoomAnimatingRef.current = false;
        });
    }, [olMap]);


    function setOverlay(sensor: SensorDataDto, preservedOffset?: { offset: [number, number], positioning: Positioning }) {
        if (!olMap) return;

        const currentZoom = olMap.getView().getZoom();
        const clusterMode = currentZoom !== undefined && isClusterMode(currentZoom);
        const shouldShow = currentZoom !== undefined && currentZoom >= 12 && !clusterMode;

        // 기존 overlay가 있는지 확인
        const existingOverlay = overlayRefs.current.get(sensor.imei);

        if (existingOverlay) {
            // 기존 overlay가 있으면 표시/숨김만 제어
            // 폴링으로 인한 데이터 업데이트 시 기존 offset 유지 (offset 15인 요소 보호)
            const element = existingOverlay.getElement();
            if (element) {
                element.style.display = shouldShow ? 'block' : 'none';
            }
            // 기존 overlay의 offset과 positioning은 그대로 유지
            return;
        }

        // 기존 Root가 있다면 먼저 정리
        const existingRoot = overlayRootsRef.current.get(sensor.imei);
        if (existingRoot) {
            existingRoot.unmount();
            overlayRootsRef.current.delete(sensor.imei);
        }

        // 클러스터 정보 확인 (줌 레벨 15 이하일 때만)
        let shouldRenderTop = false; // 말풍선이 위로 향해야 하는지

        if (currentZoom !== undefined && currentZoom <= 17) {
            // 줌 레벨이 15 이하일 때만 클러스터링 로직 적용
            const clusterInfo = clusterInfoRef.current.get(sensor.imei);
            if (clusterInfo) {
                const { sensors: clusterSensors, index: currentIndex } = clusterInfo;
                const clusterSize = clusterSensors.length;

                if (clusterSize === 2) {
                    // 2개일 경우: 아래 요소(인덱스 0)는 아래로, 위 요소(인덱스 1)는 위로
                    if (currentIndex === 0) {
                        shouldRenderTop = true; // 아래 요소는 아래에 렌더링
                    } else {
                        shouldRenderTop = false; // 위 요소는 위에 렌더링
                    }
                } else if (clusterSize === 3) {
                    // 3개일 경우: 중간 요소(인덱스 1)는 아래로, 나머지는 위로
                    if (currentIndex === 1) {
                        shouldRenderTop = true; // 중간 요소는 아래에 렌더링
                    } else {
                        shouldRenderTop = false; // 나머지는 위에 렌더링
                    }
                } else if (clusterSize >= 4) {
                    // 4개 이상일 경우: 인덱스가 짝수면 아래, 홀수면 위
                    // 0번째(짝수) = 아래, 1번째(홀수) = 위, 2번째(짝수) = 아래, ...
                    if (currentIndex % 2 === 0) {
                        shouldRenderTop = true; // 짝수 인덱스는 아래에 렌더링
                    } else {
                        shouldRenderTop = false; // 홀수 인덱스는 위에 렌더링
                    }
                }
            }
        } else if (currentZoom !== undefined && currentZoom > 19) {
            shouldRenderTop = true;
        }

        // OpenLayers가 관리할 새로운 DOM 요소 생성
        const element = document.createElement('div');
        element.id = `overlay-${sensor.imei}`;
        element.style.cursor = 'pointer';
        element.style.display = shouldShow ? 'block' : 'none';

        // 클릭 이벤트 추가
        element.addEventListener('click', (e) => {
            e.stopPropagation();
            skipNextDashboardFocusRef.current = true;
            setSelectedSensor(sensor);
            dispatch(setSelectedSensorReducer(sensor));
            // 같은 센서를 다시 클릭해도 포커스 이동
            focusToSensor(sensor);
        });

        // 클러스터 내부 요소의 positioning과 offset 설정
        // 폴링으로 인한 데이터 업데이트 시 기존 offset 정보가 있으면 우선 사용 (offset 15인 요소 보호)
        let positioning: Positioning;
        let offset: [number, number];

        if (preservedOffset && preservedOffset.offset[1] === 15) {
            // 기존 offset이 15인 경우 유지
            positioning = preservedOffset.positioning;
            offset = preservedOffset.offset;
        } else {
            // 새로운 overlay이거나 offset이 15가 아닌 경우 새로 계산
            positioning = shouldRenderTop ? 'top-center' : 'bottom-center';
            offset = shouldRenderTop ? [0, 15] : [0, -15];
        }

        // 말꼬리표 방향 설정: 아래에 렌더링되는 경우 말꼬리표는 위로
        // shouldRenderTop = true (위에 렌더링) -> tailPosition = 'bottom' (말꼬리표 아래로)
        // shouldRenderTop = false (아래에 렌더링) -> tailPosition = 'top' (말꼬리표 위로)
        const tailPosition = shouldRenderTop ? 'top' : 'bottom';

        // 먼저 빈 Overlay를 추가 (element는 비어있음)
        const overlay = new Overlay({
            element,
            position: fromLonLat([sensor.sensorLot, sensor.sensorLat]) as [number, number],
            positioning: positioning,
            offset: offset,
            stopEvent: false,
        });
        olMap.addOverlay(overlay);

        // Overlay를 저장
        overlayRefs.current.set(sensor.imei, overlay);

        // Overlay가 추가된 후 React Root 생성 및 렌더링
        const statusColor = statusToColor(processingMap.get(sensor.sensorStatusGrp) ?? "");
        const root = createRoot(element);
        // root.render(
        //     <Bubble
        //         className={MapStyle.bubble}
        //         borderRadius={1000}
        //         backgroundColor={statusColor.color}
        //         tailPosition={tailPosition}
        //     >
        //         <Padding paddingX={12} paddingY={7}>
        //             <Text size={14} weight={"semibold"} color={"white"}>
        //                 {sensor.sensorAlias}
        //             </Text>
        //         </Padding>
        //     </Bubble>
        // );

        // Root를 저장
        overlayRootsRef.current.set(sensor.imei, root);
    }

    function updateInfoOverlay(sensor: SensorDataDto) {
        if (!infoOverlayRef.current && infoOverlayElementRef.current && olMap) {
            // 최초 생성
            const overlay = new Overlay({
                element: infoOverlayElementRef.current,
                position: fromLonLat([sensor.sensorLot, sensor.sensorLat]) as [number, number],
                positioning: 'bottom-center',
                offset: [0, -15],
                stopEvent: true, // 이벤트 전파 방지
            });
            infoOverlayRef.current = overlay;
            olMap.addOverlay(overlay);
        } else if (infoOverlayRef.current) {
            // 위치만 업데이트
            infoOverlayRef.current.setPosition(
                fromLonLat([sensor.sensorLot, sensor.sensorLat]) as [number, number]
            );
        }
    }

    // 현재 View extent 내에 있는 마커들만 카운트하는 함수
    function updateVisibleMarkerCounts(olMap: OlMap, sensorDataToCount?: SensorDataDto[]): void {
        const visibleCounts: { [key: string]: number } = { "지도합계": 0, "안전": 0, "주의": 0, "경보": 0, "점검": 0 };

        // 집계는 전체 센서 데이터 기준으로 진행하되, 명시적으로 데이터가 넘어오면 해당 데이터 사용
        const dataToCount = sensorDataToCount ?? allSensorDataRef.current ?? sensorDataRef.current ?? [];

        // 데이터가 없어도 0으로 초기화된 값 반영
        if (dataToCount.length === 0) {
            dispatch(setVisibleMarkerCounts(visibleCounts));
            return;
        }

        const view = olMap.getView();
        const extent3857 = view.calculateExtent(olMap.getSize());
        const [minLon, minLat, maxLon, maxLat] = transformExtent(extent3857, "EPSG:3857", "EPSG:4326");

        dataToCount.forEach(ele => {
            const lon = ele.sensorLot;
            const lat = ele.sensorLat;

            // extent 내에 있는지 확인 (lon/lat 기준)
            if (lon >= minLon && lon <= maxLon && lat >= minLat && lat <= maxLat) {
                const statusGroup = processingMapRef.current.get(ele.sensorStatusGrp) ?? "";
                if (visibleCounts[statusGroup] !== undefined) {
                    visibleCounts[statusGroup]++;
                }
                visibleCounts["지도합계"]++;
            }
        });

        dispatch(setVisibleMarkerCounts(visibleCounts));
    }

    function setFeatures(olMap: OlMap, sensorData: SensorDataDto[]): void {
        // 이전 타이머가 있으면 취소 (빠른 필터 전환 시 이전 overlay 추가 방지)
        if (setFeaturesTimeoutRef.current) {
            clearTimeout(setFeaturesTimeoutRef.current);
            setFeaturesTimeoutRef.current = null;
        }

        // 기존 overlay의 offset 정보 보존 (폴링으로 인한 데이터 업데이트 시 offset 유지)
        const existingOverlayOffsets = new globalThis.Map<string, { offset: [number, number], positioning: Positioning }>();
        overlayRefs.current.forEach((overlay, imei) => {
            const offset = overlay.getOffset();
            const positioning = overlay.getPositioning();
            // positioning이 'top-center' 또는 'bottom-center'인 경우만 저장
            if (offset && positioning && (positioning === 'top-center' || positioning === 'bottom-center')) {
                existingOverlayOffsets.set(imei, { offset: offset as [number, number], positioning });
            }
        });

        // 기존 React Root들 정리
        overlayRootsRef.current.forEach((root) => {
            root.unmount();
        });
        overlayRootsRef.current.clear();

        // 기존 bubble overlays 제거
        overlayRefs.current.forEach((overlay, imei) => {
            olMap.removeOverlay(overlay);
        });
        overlayRefs.current.clear();

        // 기존 ping overlays 제거
        sensorPingOverlayRefs.current.forEach((pingOverlay, imei) => {
            olMap.removeOverlay(pingOverlay);
        });
        sensorPingOverlayRefs.current.clear();

        // 기존 마커 레이어 제거 (VectorLayer만)
        olMap.getLayers().getArray()
            .filter(layer => layer instanceof VectorLayer)
            .forEach(layer => olMap.removeLayer(layer));

        // 마커 소스 생성
        const vectorSource = new VectorSource();
        sensorData.forEach(ele => {
            const point_feature = new Feature({
                geometry: new Point([ele.sensorLot, ele.sensorLat]).transform('EPSG:4326', 'EPSG:3857'),
                info: ele
            });
            point_feature.setStyle(getMarkerStyle(ele));
            vectorSource.addFeature(point_feature);
        });

        // 클러스터 소스/레이어 (줌 10 이하에서만 결합)
        const currentZoom = olMap.getView().getZoom() ?? 0;
        const clusterSource = new Cluster({
            distance: isClusterMode(currentZoom) ? 130 : 0,
            source: vectorSource,
        });
        clusterSourceRef.current = clusterSource;

        const clusterLayer = new VectorLayer({
            source: clusterSource,
            style: (feature: Feature) => {
                const features = feature.get('features') as Feature[] | undefined;
                const size = features?.length ?? 1;
                const zoom = olMap.getView().getZoom() ?? currentZoom;
                // 줌 10 이하에서는 무조건 클러스터 아이콘만 표시
                if (isClusterMode(zoom)) {
                    const style = clusterStyle.clone();
                    const text = style.getText();
                    if (text) {
                        text.setText(size.toString());
                    }
                    return style;
                }
                const info = features && features[0]?.get('info') as SensorDataDto | undefined;
                if (info) {
                    return getMarkerStyle(info);
                }
                return undefined;
            },
            updateWhileAnimating: false,
            updateWhileInteracting: false,
        });
        olMap.addLayer(clusterLayer);

        // 강제 렌더링
        olMap.renderSync();

        // 클러스터 멤버십 기록 (overlay 표시에 사용)
        const membership = new globalThis.Map<string, number>();
        clusterSource.getFeatures().forEach(cf => {
            const members = cf.get('features') as Feature[] | undefined;
            const size = members?.length ?? 0;
            if (members) {
                members.forEach(m => {
                    const info = m.get('info') as SensorDataDto | undefined;
                    if (info) membership.set(info.imei, size);
                });
            }
        });
        clusterMembershipRef.current = membership;

        // 현재 View extent 내에 있는 마커들만 카운트 (필터 무관하게 전체 데이터 우선 집계)
        const countSource = allSensorDataRef.current.length ? allSensorDataRef.current : sensorData;
        updateVisibleMarkerCounts(olMap, countSource);

        // 이전 타이머가 있으면 취소 (빠른 필터 전환 시 이전 overlay 추가 방지)
        if (setFeaturesTimeoutRef.current) {
            clearTimeout(setFeaturesTimeoutRef.current);
            setFeaturesTimeoutRef.current = null;
        }

        // 줌이 10 초과일 때만 개별 핑/버블 추가
        setFeaturesTimeoutRef.current = setTimeout(() => {
            const zoomNow = olMap.getView().getZoom() ?? currentZoom;
            const clusterModeNow = isClusterMode(zoomNow);
            if (!clusterModeNow) {
                sensorData.forEach(ele => {
                    const clusterSize = clusterMembershipRef.current.get(ele.imei) ?? 1;
                    if (clusterModeNow && clusterSize > 1) return;
                    addSensorPingOverlay(ele);
                    const preservedOffset = existingOverlayOffsets.get(ele.imei);
                    setOverlay(ele, preservedOffset);
                });
            }
            setFeaturesTimeoutRef.current = null;
        }, 0);

        // MarkerInfo overlay가 있으면 맨 마지막에 다시 추가하여 최상위에 표시
        if (infoOverlayRef.current) {
            olMap.removeOverlay(infoOverlayRef.current);
            olMap.addOverlay(infoOverlayRef.current);
        }
    }

    const pointerMoveHandler = function (evt) {
        if (evt.map === null) return;

        // Overlay 위에 있는지 확인
        const target = evt.originalEvent.target as HTMLElement;
        const isOverOverlay = infoOverlayElementRef.current?.contains(target);

        // Overlay 위가 아닐 때만 커서 변경
        if (!isOverOverlay) {
            evt.map.getTargetElement()!.style.cursor = evt.map.hasFeatureAtPixel(evt.pixel) ? 'pointer' : '';
        }
    };

    const clickHandler = function (evt) {
        const feature = evt.map.forEachFeatureAtPixel(evt.pixel, (feature) => feature);
        if (feature) {
            const features = feature.get('features') as Feature[] | undefined;
            const zoom = evt.map.getView().getZoom() ?? 0;

            // 클러스터 모드에서 여러 개 묶인 경우 클러스터 중심으로 줌/이동
            if (isClusterMode(zoom) && features && features.length > 1) {
                const geometry = feature.getGeometry();
                if (geometry) {
                    const [minX, minY, maxX, maxY] = geometry.getExtent();
                    const center = [(minX + maxX) / 2, (minY + maxY) / 2] as [number, number];
                    const view = evt.map.getView();
                    isZoomAnimatingRef.current = true;
                    view.cancelAnimations();
                    view.animate({
                        center,
                        zoom: 12,
                        duration: 800,
                    }, () => {
                        isZoomAnimatingRef.current = false;
                    });
                }
                return;
            }

            // 단일 마커인 경우 (클러스터 소스의 단일 feature 포함)
            const sensorInfo =
                (features && features[0]?.get('info')) ||
                feature.get('info');
            if (sensorInfo) {
                evt.stopPropagation();
                skipNextDashboardFocusRef.current = true;
                setSelectedSensor(sensorInfo);
                dispatch(setSelectedSensorReducer(sensorInfo));
                // 동일한 포커스 로직을 재사용해 오프셋 차이로 인한 점프를 방지
                focusToSensor(sensorInfo);
            }
        } else {
            // 빈 공간 클릭 시 MarkerInfo 닫기
            isMobileRef.current && dispatch(setIsStatusBoardOpen(false));
            skipNextDashboardFocusRef.current = false;
            setSelectedSensor(null);
        }
    };

    const dragStartHandler = function (evt) {
        isMobileRef.current && dispatch(setIsStatusBoardOpen(false));
    };

    // 줌 단계 구간을 나누어 불필요한 렌더링을 줄이기 위한 헬퍼
    const getZoomBucket = (zoom?: number) => {
        if (zoom === undefined) return "unknown";
        if (zoom <= 15) return "lte15";
        if (zoom <= 17) return "15to17";
        if (zoom <= 19) return "17to19";
        return "gt19";
    };

    // 기본 맵 객체 생성
    useEffect(() => {
        // DOM 요소가 준비될 때까지 대기
        if (!olMapRef.current) return;

        const map = new OlMap({
            target: olMapRef.current,
            layers: [
                new TileLayer({
                    source: new OSM(),
                }),
            ],
            view: new View({
                center: fromLonLat([129.34416507581582, 35.55081913598213]),
                zoom: 2,
                extent: MAP_EXTENT,
                maxZoom: 19
            }),
        });
        map.on('pointermove', pointerMoveHandler);
        map.on('pointerdrag', dragStartHandler);
        map.on('click', clickHandler);

        // view의 extent가 변경될 때마다 store 업데이트 및 마커 카운트 재계산
        const updateExtent = () => {
            const view = map.getView();
            const extent = view.calculateExtent(map.getSize());
            dispatch(setMapExtent(extent as [number, number, number, number]));

            // View가 변경될 때마다 현재 View 내 마커 수 재계산 (전체 데이터 우선)
            const countSource = allSensorDataRef.current.length ? allSensorDataRef.current : sensorDataRef.current;
            updateVisibleMarkerCounts(map, countSource);
        };

        // 줌 레벨 변경 감지
        const updateZoomLevel = () => {
            const view = map.getView();
            const zoom = view.getZoom();
            if (zoom !== undefined) {
                const bucket = getZoomBucket(zoom);
                const shouldShowOverlay = zoom > 12;
                
                // 줌 버킷이 변경되거나, 오버레이 표시 여부가 변경될 때 업데이트
                if (bucket !== zoomBucketRef.current || shouldShowOverlay !== isOverlayVisibleRef.current) {
                    zoomBucketRef.current = bucket;
                    isOverlayVisibleRef.current = shouldShowOverlay;
                    updateOverlaysForZoomRef.current();
                }
                // 줌 레벨이 15 이하가 되면 marker info 닫기 (뒤로가기 버튼과 동일한 로직)
                // 단, 줌 애니메이션 중이면 닫지 않음 (마커 클릭으로 인한 줌인 중에는 닫지 않도록)
                if (zoom <= 15 && !isZoomAnimatingRef.current) {
                    dispatch(setDashboardSelectedSensor(null));
                }
            }
        };

        // 초기 extent 설정 (카운트는 제외 - sensorData가 준비된 후에만)
        const view = map.getView();
        const extent = view.calculateExtent(map.getSize());
        dispatch(setMapExtent(extent as [number, number, number, number]));
        updateZoomLevel();

        // view 변경 이벤트 리스너 등록
        const changeResolutionHandler = () => {
            updateZoomLevel();
            updateExtent(); // 줌 변경 시에도 카운트 재계산
        };

        map.on('moveend', updateExtent);
        map.getView().on('change:resolution', changeResolutionHandler);

        setOlMap(map);

        return () => {
            // React Root 정리
            setTimeout(() => {
                overlayRootsRef.current.forEach((root) => {
                    root.unmount();
                });
                overlayRootsRef.current.clear();
                overlayRefs.current.clear();
            });

            if (map) {
                map.un('pointermove', pointerMoveHandler);
                map.un('pointerdrag', dragStartHandler);
                map.un('click', clickHandler);
                map.un('moveend', updateExtent);
                map.getView().un('change:resolution', changeResolutionHandler);
                map.setTarget(undefined);
            }
        };
    }, [])

    // isMobile 변경 시 View의 extent 업데이트
    useEffect(() => {
        if (olMap) {
            const view = olMap.getView();
            // View의 extent를 업데이트하기 위해 새로운 View 생성
            const newView = new View({
                center: view.getCenter(),
                zoom: view.getZoom(),
                extent: MAP_EXTENT,
                maxZoom: 19
            });
            olMap.setView(newView);

            // 이벤트 리스너 다시 등록
            const updateExtent = () => {
                const currentView = olMap.getView();
                const extent = currentView.calculateExtent(olMap.getSize());
                dispatch(setMapExtent(extent as [number, number, number, number]));

                // View가 변경될 때마다 현재 View 내 마커 수 재계산 (전체 데이터 우선)
                const countSource = allSensorDataRef.current.length ? allSensorDataRef.current : sensorDataRef.current;
                updateVisibleMarkerCounts(olMap, countSource);
            };
            const updateZoomLevel = () => {
                const currentView = olMap.getView();
                const zoom = currentView.getZoom();
                if (zoom !== undefined) {
                    const bucket = getZoomBucket(zoom);
                    const shouldShowOverlay = zoom > 12;
                    
                    // 줌 버킷이 변경되거나, 오버레이 표시 여부가 변경될 때 업데이트
                    if (bucket !== zoomBucketRef.current || shouldShowOverlay !== isOverlayVisibleRef.current) {
                        zoomBucketRef.current = bucket;
                        isOverlayVisibleRef.current = shouldShowOverlay;
                        updateOverlaysForZoomRef.current();
                    }
                    if (zoom <= 15 && !isZoomAnimatingRef.current) {
                        dispatch(setDashboardSelectedSensor(null));
                    }
                }
            };

            const changeResolutionHandler = () => {
                updateZoomLevel();
                updateExtent(); // 줌 변경 시에도 카운트 재계산
                const zoom = olMap.getView().getZoom() ?? 0;
                if (clusterSourceRef.current) {
                    clusterSourceRef.current.setDistance(isClusterMode(zoom) ? 130 : 0);
                    clusterSourceRef.current.refresh();
                }
                updateOverlaysForZoomRef.current();
            };

            // 기존 이벤트 리스너 제거 후 새로 등록
            olMap.un('moveend', updateExtent);
            olMap.getView().un('change:resolution', changeResolutionHandler);

            olMap.on('moveend', () => {
                updateExtent();
                updateOverlaysForZoomRef.current();
            });
            newView.on('change:resolution', changeResolutionHandler);
        }
    }, [olMap, MAP_EXTENT, isMobile])

    useEffect(() => {
        // 두 API 모두 성공하고 맵이 준비되었을 때만 실행
        if (olMap) {
            // 이전 타이머가 있으면 취소 (빠른 필터 전환 시 이전 작업 취소)
            if (updateFeaturesTimeoutRef.current) {
                clearTimeout(updateFeaturesTimeoutRef.current);
                updateFeaturesTimeoutRef.current = null;
            }
            if (setFeaturesTimeoutRef.current) {
                clearTimeout(setFeaturesTimeoutRef.current);
                setFeaturesTimeoutRef.current = null;
            }

            // 기존 React Root들 정리 (setTimeout으로 비동기 처리하여 렌더링 사이클 충돌 방지)
            updateFeaturesTimeoutRef.current = setTimeout(() => {
                overlayRootsRef.current.forEach((root) => {
                    root.unmount();
                });
                overlayRootsRef.current.clear();
                overlayRefs.current.clear();

                // 내 위치 마커와 InfoOverlay를 제외한 모든 오버레이 제거
                const overlaysToKeep = [myLocationOverlayRef.current, infoOverlayRef.current].filter(Boolean);
                const allOverlays = olMap.getOverlays().getArray().slice();
                allOverlays.forEach(overlay => {
                    if (!overlaysToKeep.includes(overlay)) {
                        olMap.removeOverlay(overlay);
                    }
                });

                setFeatures(olMap, displayedSensorData);

                // 초기 로드 또는 SensorStatusFloating에서 필터 클릭 시 모든 센서가 보이도록 지도 fit
                // shouldFitOnFilterChange가 true이고 데이터가 있으면 fit 실행
                // 단, 현재 데이터가 현재 필터 조건와 일치하는지 확인 (Race Condition 방지)
                const isDataSyncedWithFilter = true;

                const shouldFit = displayedSensorData.length > 0 && (!hasInitialLoaded || (shouldFitOnFilterChange && isDataSyncedWithFilter));

                if (shouldFit) {
                    // 모든 센서의 경도/위도에서 최소/최대값 계산
                    let minLon = displayedSensorData[0].sensorLot;
                    let maxLon = displayedSensorData[0].sensorLot;
                    let minLat = displayedSensorData[0].sensorLat;
                    let maxLat = displayedSensorData[0].sensorLat;

                    displayedSensorData.forEach(sensor => {
                        if (sensor.sensorLot < minLon) minLon = sensor.sensorLot;
                        if (sensor.sensorLot > maxLon) maxLon = sensor.sensorLot;
                        if (sensor.sensorLat < minLat) minLat = sensor.sensorLat;
                        if (sensor.sensorLat > maxLat) maxLat = sensor.sensorLat;
                    });

                    // 경도/위도 범위를 Web Mercator 좌표계로 변환
                    const extent = transformExtent(
                        [minLon, minLat, maxLon, maxLat],
                        'EPSG:4326',
                        'EPSG:3857'
                    );

                    // 지도를 해당 범위에 맞춤 (여백 포함)
                    const view = olMap.getView();
                    view.fit(extent, {
                        duration: 1000,
                        padding: [300, 300, 300, 300], // 대시보드 열고 닫는 것과 무관하게 고정 padding 사용
                        maxZoom: 16, // 너무 가까이 줌되지 않도록 제한
                        callback: () => {
                            // fit 완료 후 카운트 업데이트 및 초기 로드 완료 표시
                            // 집계는 전체 데이터 기준
                            updateVisibleMarkerCounts(olMap, allSensorDataRef.current.length ? allSensorDataRef.current : displayedSensorData);
                            if (!hasInitialLoaded) {
                                setHasInitialLoaded(true); // fit 애니메이션 완료 후 초기 로드 완료 표시
                            }
                            // SensorStatusFloating에서 필터 변경으로 인한 fit이었다면 플래그 리셋 및 ref 업데이트
                            if (shouldFitOnFilterChange) {
                                dispatch(setShouldFitOnFilterChange(false));
                                prevFilterRef.current = sensorDashboardFilter;
                            }
                        }
                    });
                } else {
                    // fit이 실행되지 않아도 카운트 업데이트
                    updateVisibleMarkerCounts(olMap, allSensorDataRef.current.length ? allSensorDataRef.current : displayedSensorData);
                    // sensorData가 있지만 fit이 실행되지 않은 경우 (이미 초기 로드 완료)
                    if (displayedSensorData.length > 0 && hasInitialLoaded === false) {
                        setHasInitialLoaded(true);
                    }
                    // shouldFitOnFilterChange 플래그가 설정되어 있었지만 fit이 실행되지 않은 경우
                    // 데이터가 없으면 플래그 리셋, 데이터가 있으면 다음 렌더링에서 fit이 실행되도록 플래그 유지
                    if (shouldFitOnFilterChange) {
                        if (displayedSensorData.length === 0) {
                            // 데이터가 없으면 플래그 리셋
                            dispatch(setShouldFitOnFilterChange(false));
                        }
                        // 데이터가 있으면 플래그 유지 (다음 렌더링에서 fit 실행)
                    }
                }
                updateFeaturesTimeoutRef.current = null;
            }, 0);
        }

        // cleanup: 타이머 취소
        return () => {
            if (updateFeaturesTimeoutRef.current) {
                clearTimeout(updateFeaturesTimeoutRef.current);
                updateFeaturesTimeoutRef.current = null;
            }
            if (setFeaturesTimeoutRef.current) {
                clearTimeout(setFeaturesTimeoutRef.current);
                setFeaturesTimeoutRef.current = null;
            }
        };
    }, [
        displayedSensorData,
        processingMap,
        olMap,
        hasInitialLoaded,
        isMobile,
        sensorDashboardFilter,
        shouldFitOnFilterChange,
    ])

    // 필터 변경 시 팝업 닫기
    useEffect(() => {
        if (prevFilterRef.current !== sensorDashboardFilter) {
            setSelectedSensor(null);
            prevFilterRef.current = sensorDashboardFilter;
        }
    }, [sensorDashboardFilter]);

    // 줌 레벨 변경 시 overlay 업데이트 로직을 함수로 분리
    const updateOverlaysForZoom = useCallback(() => {
        if (!olMap) return;

        const currentZoom = olMap.getView().getZoom();
        const shouldShow = currentZoom !== undefined && currentZoom >= 12;

        // Mobile Dynamic Clustering Removed
        // 모바일에서도 PC와 동일하게 고정 거리(230m) 클러스터링을 사용하기 위해
        // 줌 레벨에 따른 동적 재계산 로직을 제거함.
        // 이제 클러스터링은 setFeatures(데이터 변경) 시에만 계산됨.

        // 모든 overlay의 display, positioning, offset 업데이트
        overlayRefs.current.forEach((overlay, imei) => {
            const element = overlay.getElement();
            if (element) {
                element.style.display = shouldShow ? 'block' : 'none';
            }

            // 센서 정보 찾기
            const sensor = sensorDataRef.current.find(s => s.imei === imei);
            if (sensor) {
                // 클러스터 정보 확인 (줌 레벨 15 이하일 때만)
                let shouldRenderTop = false;

                if (currentZoom !== undefined && currentZoom <= 17) {
                    // 줌 레벨이 15 이하일 때만 클러스터링 로직 적용
                    const clusterInfo = clusterInfoRef.current.get(imei);
                    if (clusterInfo) {
                        const { sensors: clusterSensors, index: currentIndex } = clusterInfo;
                        const clusterSize = clusterSensors.length;

                        if (clusterSize === 2) {
                            // 2개일 경우: 아래 요소(인덱스 0)는 아래로, 위 요소(인덱스 1)는 위로
                            if (currentIndex === 0) {
                                shouldRenderTop = true; // 아래 요소는 아래에 렌더링
                            } else {
                                shouldRenderTop = false; // 위 요소는 위에 렌더링
                            }
                        } else if (clusterSize === 3) {
                            // 3개일 경우: 중간 요소(인덱스 1)는 아래로, 나머지는 위로
                            if (currentIndex === 1) {
                                shouldRenderTop = true; // 중간 요소는 아래에 렌더링
                            } else {
                                shouldRenderTop = false; // 나머지는 위에 렌더링
                            }
                        } else if (clusterSize >= 4) {
                            // 4개 이상일 경우: 인덱스가 짝수면 아래, 홀수면 위
                            if (currentIndex % 2 === 0) {
                                shouldRenderTop = true; // 짝수 인덱스는 아래에 렌더링
                            } else {
                                shouldRenderTop = false; // 홀수 인덱스는 위에 렌더링
                            }
                        }
                    }
                } else if (currentZoom !== undefined && currentZoom > 19) {
                    // 줌 레벨이 15 이상일 때는 모두 위에 렌더링
                    shouldRenderTop = false;
                }

                // positioning과 offset 업데이트
                const positioning = shouldRenderTop ? 'top-center' : 'bottom-center';
                const offset = shouldRenderTop ? [0, 15] : [0, -15];

                overlay.setPositioning(positioning);
                overlay.setOffset(offset);

                // 말꼬리표 방향도 업데이트 (React Root 다시 렌더링)
                const root = overlayRootsRef.current.get(imei);
                if (root) {
                    try {
                        const tailPosition = shouldRenderTop ? 'top' : 'bottom';
                        const statusColor = statusToColor(processingMap.get(sensor.sensorStatusGrp) ?? "");
                        const element = overlay.getElement();
                        // element가 여전히 DOM에 있는지 확인
                        if (element && element.isConnected) {
                            // root.render(
                            //     <Bubble
                            //         className={MapStyle.bubble}
                            //         borderRadius={1000}
                            //         backgroundColor={statusColor.color}
                            //         tailPosition={tailPosition}
                            //     >
                            //         <Padding paddingX={12} paddingY={7}>
                            //             <Text size={14} weight={"semibold"} color={"white"}>
                            //                 {sensor.sensorAlias}
                            //             </Text>
                            //         </Padding>
                            //     </Bubble>
                            // );
                        }
                    } catch (error) {
                    }
                }
            }
        });

        // 핑 오버레이 표시/숨김
        sensorPingOverlayRefs.current.forEach((pingOverlay) => {
            const el = pingOverlay.getElement() as HTMLElement | undefined;
            if (el) {
                el.style.display = shouldShow ? 'block' : 'none';
            }
        });
    }, [olMap, processingMap]);

    // 최신 콜백을 보관해서 이벤트 핸들러에서 사용할 수 있도록 갱신
    updateOverlaysForZoomRef.current = updateOverlaysForZoom;

    // selectedSensor 변경 시 MarkerInfo 표시
    useEffect(() => {
        if (selectedSensor && olMap) {
            updateInfoOverlay(selectedSensor);
        } else if (!selectedSensor && infoOverlayRef.current && olMap) {
            // selectedSensor가 null이면 InfoOverlay 제거
            olMap.removeOverlay(infoOverlayRef.current);
            infoOverlayRef.current = null;
        }
    }, [selectedSensor, olMap])

    useEffect(() => {
        if (!olMap) return;

        if (dashboardSelectedSensor) {
            setSelectedSensor(dashboardSelectedSensor);

            if (skipNextDashboardFocusRef.current) {
                skipNextDashboardFocusRef.current = false;
                return;
            }

            focusToSensor(dashboardSelectedSensor);
        } else {
            setSelectedSensor(null);
            skipNextDashboardFocusRef.current = false;
        }
    }, [dashboardSelectedSensor, olMap, focusToSensor])

    // 내 위치 업데이트 함수
    const updateMyLocation = () => {
        if (!olMap) return;

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                const center = fromLonLat([longitude, latitude]) as [number, number];

                // 내 위치 마커 오버레이 생성 또는 업데이트
                if (!myLocationOverlayRef.current && myLocationElementRef.current) {
                    const overlay = new Overlay({
                        element: myLocationElementRef.current,
                        position: center,
                        positioning: 'center-center',
                        stopEvent: false,
                    });
                    myLocationOverlayRef.current = overlay;
                    olMap.addOverlay(overlay);
                } else if (myLocationOverlayRef.current) {
                    myLocationOverlayRef.current.setPosition(center);
                }
            },
            (error) => {
            },
            {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 0
            }
        );
    };

    // 10초마다 위치 업데이트 (컴포넌트 마운트 시 시작)
    useEffect(() => {
        if (!olMap) return;

        // 즉시 실행
        updateMyLocation();

        // 10초마다 폴링
        locationTrackingIntervalRef.current = setInterval(() => {
            updateMyLocation();
        }, 10000);

        return () => {
            if (locationTrackingIntervalRef.current) {
                clearInterval(locationTrackingIntervalRef.current);
            }
        };
    }, [olMap]);


    const handleMyLocation = () => {
        if (!olMap) return;

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    const offsetX = isMobile ? 0 : 0.00000;
                    const offsetY = isMobile ? 0.0006 : 0;
                    const center = fromLonLat([longitude - offsetX, latitude - offsetY]) as [number, number];
                    // 맵 중심 이동 (애니메이션)
                    const view = olMap.getView();
                    view.animate({
                        center: center,
                        zoom: 18,
                        duration: 1000
                    });

                    // 마커도 즉시 업데이트
                    if (!myLocationOverlayRef.current && myLocationElementRef.current) {
                        const overlay = new Overlay({
                            element: myLocationElementRef.current,
                            position: center,
                            positioning: 'center-center',
                            stopEvent: false,
                        });
                        myLocationOverlayRef.current = overlay;
                        olMap.addOverlay(overlay);
                    } else if (myLocationOverlayRef.current) {
                        myLocationOverlayRef.current.setPosition(center);
                    }
                },
                (error) => {
                    openAlert({ title: "오류", text: "위치 정보를 가져올 수 없습니다. 브라우저의 위치 권한을 확인해 주세요.", type: "error" })
                },
                {
                    enableHighAccuracy: true,
                    timeout: 5000,
                    maximumAge: 0
                }
            );
        } else {
            openAlert({ title: "알림", text: "이 브라우저에서는 위치 정보를 지원하지 않습니다." })
        }
    };


    return (
        <>
            <Loading isLoading={!forceHideLoading && !hasInitialLoaded} minDuration={1200} message="Loading..." />
            <div id="olMap" className={MapStyle.olMap} ref={olMapRef}></div>
            <div className={MapStyle.myLocationBtn} onClick={handleMyLocation} title="My Location">
                <IcoGeolocation width={22} height={22} stroke="#666" />
            </div>
            <SensorStatusFloating isStatusBoardOpen={false} />
            <SensorStatusMobile isStatusBoardOpen={isStatusBoardOpen} />
            <SensorStatusBoard
                isOpen={isStatusBoardOpen}
                setOpen={(open) => dispatch(setIsStatusBoardOpen(open))}
            />
            {/* MarkerInfo Overlay를 위한 DOM 요소 */}
            <div style={{ display: "none" }}>
                <div
                    ref={infoOverlayElementRef}
                    style={{
                        display: selectedSensor ? 'block' : 'none',
                        position: 'relative',
                        zIndex: 100,
                        pointerEvents: 'auto',
                        cursor: 'auto'
                    }}
                >
                    {selectedSensor && <MarkerInfo {...selectedSensor} />}
                </div>
                {/* 내 위치 마커 DOM 요소 */}
                <div
                    ref={myLocationElementRef}
                    className={MapStyle.myLocationMarker}
                ></div>
            </div>
        </>
    )
}

export default Map;
